import { Link } from "react-router-dom";
import { ArrowRight, Compass, HandHeart, Eye, Scale } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import Newsletter from "@/components/Newsletter";
import { IMAGES } from "@/data/content";

const VALUES = [
  {
    icon: Compass,
    title: "Curation over aggregation",
    body: "We publish fewer recommendations on purpose. Every stay, tool, and tour is chosen for character, usefulness, and honest value.",
  },
  {
    icon: Eye,
    title: "Transparency by default",
    body: "Affiliate relationships are disclosed clearly, and commissions never decide our rankings. If something has a limitation, we say so.",
  },
  {
    icon: Scale,
    title: "Every budget, taken seriously",
    body: "Quality isn't luxury-only. We recommend across price points and judge each option against what it promises.",
  },
  {
    icon: HandHeart,
    title: "Practical above all",
    body: "Itineraries you can actually follow, budgets without fantasy math, and packing lists that respect carry-on reality.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="relative grain-overlay overflow-hidden">
        <div className="absolute top-0 left-1/4 h-[380px] w-[380px] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 sm:pt-44 pb-16 sm:pb-20 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>About Venture &amp; Stay</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl text-navy tracking-tight leading-[1.08]">
              Better journeys, <span className="italic text-ocean">thoughtfully planned.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-charcoal/75 leading-relaxed">
              Venture &amp; Stay is a professional travel-planning publication that helps travelers choose
              exceptional places to stay, build practical itineraries, and book with confidence. We combine
              destination guidance, honest hotel comparisons, smart travel tools, and curated experiences for
              travelers who value both quality and value.
            </p>
            <p className="mt-4 text-base text-charcoal/75 leading-relaxed">
              We're independent researchers first and publishers second. Our promise is simple: clear travel
              advice, well-researched recommendations, better stays, and smoother journeys.
            </p>
            <Link
              to="/affiliate-disclosure"
              data-testid="about-disclosure-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold transition-colors duration-300"
            >
              Read how our affiliate partnerships work
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="rounded-[2rem] overflow-hidden border-4 border-white shadow-[0_40px_80px_-24px_rgba(16,42,67,0.35)]">
                <img
                  src={IMAGES.poolLounge}
                  alt="A calm hotel pool lounge at dusk"
                  className="h-[420px] sm:h-[500px] w-full object-cover"
                  data-testid="about-image"
                />
              </div>
              <div className="absolute -bottom-6 left-6 rounded-2xl bg-navy text-ivory px-6 py-4 shadow-xl">
                <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-gold">Est. 2026</p>
                <p className="mt-1 font-serif text-lg">Published with care, not volume.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 sm:pb-28">
        <Reveal>
          <Eyebrow>Editorial Values</Eyebrow>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-navy">How we decide what to recommend</h2>
        </Reveal>
        <div className="mt-12 grid sm:grid-cols-2 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div
                data-testid={`value-${i + 1}`}
                className="group h-full rounded-3xl bg-white border border-slateblue/15 p-8 transition-[transform,border-color,box-shadow] duration-500 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-navy text-gold transition-colors duration-500 group-hover:bg-gold group-hover:text-navy">
                  <v.icon className="h-5 w-5" />
                </span>
                <h3 className="mt-6 font-serif text-2xl text-navy">{v.title}</h3>
                <p className="mt-3 text-sm sm:text-base text-charcoal/70 leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
      <Newsletter />
    </div>
  );
}
